import React, { useCallback, useState } from "react";
import { useSetRecoilState } from "recoil";
// material
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// __apis__
import { registerMealIntakeRequest } from "src/__apis__/mealsTracking";
// hooks
import useMealsInTakeTrackingStatus from "src/hooks/useMealsInTakeTrackingStatus";
// atoms
import alertAtom from "src/recoil/atoms/alertAtom";
// components
import UploadSingleFile from "./upload/UploadSingleFile";
import Iconify from "./Iconify";
import useLocales from "src/hooks/useLocales";

// ------------------------------------------------------------------------------------------------------------------

const CancelMealImageUpload = ({
  isTriggered,
  closeHandler,
  handleUploadMeal,
  loading,
}) => {
  const { translate } = useLocales();

  return (
    <Dialog open={isTriggered} onClose={closeHandler}>
      <DialogTitle>
        {translate(
          "componentsTranslations.cancelUploadMealPopUpTranslations.title"
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {translate(
            "componentsTranslations.cancelUploadMealPopUpTranslations.description"
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeHandler} variant="outlined" color="success">
          {translate(
            "componentsTranslations.cancelUploadMealPopUpTranslations.actionButtons.cancel"
          )}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          endIcon={<Iconify icon="mdi:emoticon-sad-outline" />}
          onClick={handleUploadMeal}
          loading={loading}
        >
          {translate(
            "componentsTranslations.cancelUploadMealPopUpTranslations.actionButtons.submit"
          )}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------------------------------------------------------

function UploadMealPopUp({ isTriggered, closeHandler, mealId }) {
  const theme = useTheme();

  const [cancelMealUploadPopUp, triggerCancelMealUploadPopUp] = useState(false);

  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);

  const triggerAlert = useSetRecoilState(alertAtom);

  const { translate } = useLocales();

  const [mealsTrackingStatusFetcher, mealsTrackingStatus] =
    useMealsInTakeTrackingStatus();

  const handleUploadMeal = useCallback(async () => {
    setLoading(true);
    const data = new FormData();
    if (file) {
      data.append("mealImage", file);
    }
    data.append("mealId", mealId);

    registerMealIntakeRequest(data)
      .then((response) => {
        setLoading(false);
        triggerAlert({
          triggered: true,
          type: "success",
          message: "Meal submitted successfully",
        });
        closeHandler();
        mealsTrackingStatusFetcher();
      })
      .catch((error) => {
        console.log("Error tracking meal", error);
        setLoading(false);
        triggerAlert({
          triggered: true,
          message: "Error tracking meal",
          type: "error",
        });
      });
  }, [file, mealId]);

  const handleDropSingleFile = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
    }
  }, []);

  return (
    <Dialog open={isTriggered} onClose={closeHandler} fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{ width: 50, mr: 2 }}
              component="img"
              src="/static/icons/gift-box.png"
            />
            <Typography variant="h4">
              {translate(
                "componentsTranslations.uploadMealPopUpTranslations.title"
              )}
            </Typography>
          </Box>
          <Typography
            sx={{ mt: 2, color: theme.palette.grey[600] }}
            variant="body1"
          >
            {translate(
              "componentsTranslations.uploadMealPopUpTranslations.description"
            )}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mt={2}>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <UploadSingleFile
                file={file}
                onDrop={handleDropSingleFile}
                title="Upload Meal Image"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Iconify icon="mdi:emoticon-sad-outline" />}
          variant="outlined"
          onClick={() => triggerCancelMealUploadPopUp(true)}
          color="error"
        >
          {translate(
            "componentsTranslations.uploadMealPopUpTranslations.actionButtons.decline"
          )}
        </Button>
        <LoadingButton
          endIcon={<Iconify icon="ic:outline-send" />}
          variant="contained"
          loading={loading}
          onClick={handleUploadMeal}
        >
          {translate(
            "componentsTranslations.uploadMealPopUpTranslations.actionButtons.submit"
          )}
        </LoadingButton>
      </DialogActions>
      {/* Cancel upload meal image pop up */}
      <CancelMealImageUpload
        isTriggered={cancelMealUploadPopUp}
        handleUploadMeal={handleUploadMeal}
        loading={loading}
        closeHandler={() => triggerCancelMealUploadPopUp(false)}
      />
    </Dialog>
  );
}

export default UploadMealPopUp;
